import React, { useMemo } from "react";
import "./i18n";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes } from "react-router-dom";
import { FaEnvelope, FaFacebook, FaInstagram, FaMapMarkerAlt, FaPhone, FaYoutube } from "react-icons/fa";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScreenshotSlider from "./components/ScreenshotSlider";
import VideoTutorial from "./components/VideoTutorial";

function App() {
  const { t } = useTranslation();

  const CompactPageTitle = ({ children }) => (
    <div className="mb-5 text-center">
      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-600 dark:from-white dark:to-slate-300 bg-clip-text text-transparent mb-2 px-1">
        {children}
      </h2>
      <div className="w-14 h-0.5 bg-gradient-to-r from-sky-500 to-indigo-600 mx-auto rounded-full" />
    </div>
  );

  const heroLeftImageSrc = "/hero-devices.png";
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = "/downloads/business-finance-setup.exe";
    link.download = "business-finance-setup.exe";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const pageLinks = useMemo(
    () => [
      { to: "/services", label: t("nav.services") },
      { to: "/features", label: t("nav.features") },
      { to: "/download", label: t("nav.download") },
      { to: "/about", label: t("nav.about") },
      { to: "/support", label: t("nav.support") },
    ],
    [t]
  );

  const HomePage = () => {
    const marqueeRaw = t("home.marquee", { returnObjects: true });
    const heroMarquee =
      Array.isArray(marqueeRaw) && marqueeRaw.length > 0 && typeof marqueeRaw[0]?.name === "string"
        ? marqueeRaw
        : [];
    const heroRow = heroMarquee.length ? [...heroMarquee, ...heroMarquee] : [];

    return (
    <>
      <div className="relative overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src="/Background.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-slate-900/55 to-slate-900/70 dark:from-slate-950/80 dark:via-slate-950/70 dark:to-slate-950/85" />
        <div className="absolute inset-0">
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-slate-300/20 dark:bg-slate-700/20 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>
        {/* <div className="hidden xl:block absolute left-0 top-1/2 -translate-y-1/2 z-10 pointer-events-none"> */}

        <div className="hidden xl:block absolute left-0 z-10 pointer-events-none">
          <img
            src={heroLeftImageSrc}
            alt="Business Finance preview"
            className="w-[320px] 2xl:w-[380px] h-auto object-contain opacity-95"
          />
        </div>

        <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-8 sm:pt-3 sm:pb-10">
          <div className="text-center">
            <div className="relative mb-10 sm:mb-16 lg:mb-24 hidden lg:block">
              <div className="absolute right-0 -top-16 text-right space-y-1 promo-float">
                <p className="text-base md:text-lg italic font-semibold text-slate-100 animate-bounce">
                  &quot;{t("home.promoQuote")}&quot;
                </p>
              </div>
            </div>

            <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold text-white drop-shadow-[0_8px_30px_rgba(0,0,0,0.5)] mb-6 sm:mb-8 leading-tight py-3 sm:py-4 px-1">
              {t("title")}
            </h1>

            <p className="text-base sm:text-xl md:text-2xl text-slate-100/95 mb-6 max-w-4xl mx-auto leading-relaxed font-light drop-shadow-[0_2px_12px_rgba(0,0,0,0.35)] px-1">
              {t("description")}
            </p>

            <div className="">
              <button
                onClick={handleDownload}
                className="group relative inline-flex max-w-full items-center justify-center px-6 py-4 text-base sm:px-12 sm:py-5 sm:text-xl font-semibold text-white bg-gradient-to-r from-green-500 via-emerald-500 to-teal-600 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-500 ease-in-out overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <svg className="w-6 h-6 sm:w-7 sm:h-7 mr-2 sm:mr-4 shrink-0 group-hover:animate-bounce relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="relative z-10 text-center leading-snug">{t("download")} {t("home.downloadSuffix")}</span>
              </button>
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t("supportedOS")}
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  {t("fileSize")}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <div className="relative z-[2] w-full border-t border-slate-300/70 dark:border-slate-600/70 bg-gradient-to-b from-white/40 via-orange-50/30 to-transparent dark:from-slate-900/20 dark:via-slate-800/30 dark:to-transparent pt-3 pb-4">
        <div className="flex flex-col items-center px-2 mb-2 max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-3 sm:gap-5 w-full">
            <span className="h-[2px] flex-1 rounded-full bg-gradient-to-r from-transparent via-orange-300 to-orange-400/80 dark:via-orange-500/60 dark:to-orange-400/80 max-w-[28%]" />
            <h3 className="text-center text-[10px] sm:text-[11px] md:text-sm lg:text-base font-extrabold uppercase tracking-[0.12em] sm:tracking-[0.26em] md:tracking-[0.32em] bg-gradient-to-r from-slate-800 via-orange-600 to-slate-800 dark:from-slate-100 dark:via-orange-300 dark:to-slate-100 bg-clip-text text-transparent whitespace-normal sm:whitespace-nowrap px-1 leading-tight">
              {t("home.ourServices")}
            </h3>
            <span className="h-[2px] flex-1 rounded-full bg-gradient-to-l from-transparent via-orange-300 to-orange-400/80 dark:via-orange-500/60 dark:to-orange-400/80 max-w-[28%]" />
          </div>
        </div>
        <div className="w-full overflow-hidden whitespace-nowrap">
          <div className="services-marquee-track">
            {heroRow.map((service, index) => (
              <span
                key={`${service.name}-${index}`}
                className="inline-flex items-center mx-4 sm:mx-8 text-slate-700 dark:text-slate-200 text-xs sm:text-sm md:text-base font-semibold uppercase tracking-wide"
              >
                <span className="mr-2 text-base sm:text-lg shrink-0" aria-hidden="true">{service.icon}</span>
                {service.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </>
    );
  };

  const ServicesPage = () => {
    const cardsRaw = t("services.cards", { returnObjects: true });
    const cards = Array.isArray(cardsRaw) ? cardsRaw : [];
    const icons = ["🧾", "📦", "📊", "🔄", "🎓", "🛠️"];
    
    const marqueeRaw = t("home.marquee", { returnObjects: true });
    const servicesMarquee =
      Array.isArray(marqueeRaw) && marqueeRaw.length > 0 && typeof marqueeRaw[0]?.name === "string"
        ? marqueeRaw
        : [];

    return (
    <div className="py-5 md:py-6 relative">
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50/60 via-amber-50/40 to-orange-100/30 dark:from-slate-900/70 dark:via-slate-800/60 dark:to-slate-900/50" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-200/20 dark:bg-orange-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-200/20 dark:bg-amber-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <CompactPageTitle>{t("services.title")}</CompactPageTitle>
          <p className="text-base md:text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto mb-4 leading-relaxed">
            {t("services.intro")}
          </p>
        </div>
        {/* Industry Cards Section */}
        <div className="mb-12">
          <div className="flex flex-col items-center px-2 max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-4 sm:gap-6 w-full">
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {servicesMarquee.map((service, index) => (
              <div
                key={`${service.name}-${index}`}
                className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-md rounded-2xl p-4 shadow-lg border border-orange-200/80 dark:border-slate-600/80 hover:shadow-2xl hover:scale-110 hover:border-orange-400 dark:hover:border-orange-400 transition-all duration-400 ease-out group"
                style={{
                  animation: `fadeInUp 0.6s ease-out ${index * 0.08}s both`
                }}
              >
                <div className="text-3xl mb-3 text-center transform group-hover:scale-125 group-hover:rotate-12 transition-transform duration-400">{service.icon}</div>
                <h4 className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-white text-center leading-snug group-hover:text-orange-600 dark:group-hover:text-orange-300 transition-colors duration-400">
                  {service.name}
                </h4>
              </div>
            ))}
          </div>
        </div>

        {/* Service Cards Section */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((service, index) => (
            <div
              key={service.title || index}
              className="relative bg-white/90 dark:bg-slate-800/90 backdrop-blur-md rounded-3xl p-6 shadow-xl border-2 border-orange-400/50 shadow-orange-400/40"
            >
              <div className="text-4xl mb-4">{icons[index] ?? "✨"}</div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{service.title}</h3>
              <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">{service.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
    );
  };

  const AboutPage = () => (
    <>
      <div className="py-5 md:py-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-orange-50/30 to-transparent dark:via-slate-800/30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CompactPageTitle>{t("aboutTitle")}</CompactPageTitle>

          <div className="grid lg:grid-cols-5 gap-5 lg:gap-6">
            <div className="lg:col-span-2 bg-white/85 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl p-5 shadow-md border border-orange-100/70 dark:border-slate-700">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{t("aboutPage.founderProfile")}</h3>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 text-white font-bold text-xl flex items-center justify-center shadow-md shrink-0">
                  AH
                </div>
                <div className="min-w-0">
                  <p className="text-base font-semibold text-slate-900 dark:text-white">Arif Hussain</p>
                  <p className="text-xs text-slate-600 dark:text-slate-300 leading-snug">{t("aboutPage.founderRole")}</p>
                </div>
              </div>
              <p className="text-slate-600 dark:text-slate-300 leading-snug text-sm">
                {t("aboutPage.founderBio")}
              </p>
            </div>

            <div className="lg:col-span-3 bg-white/85 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl p-5 md:p-6 shadow-md border border-orange-100/70 dark:border-slate-700">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{t("aboutPage.ourAim")}</h3>
              <p className="text-slate-600 dark:text-slate-300 leading-snug text-sm md:text-[15px] mb-5">
                {t("aboutPage.ourAimParagraph")}
              </p>

              <div className="rounded-xl p-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md">
                <h4 className="text-lg font-bold mb-1.5">{t("aboutPage.companyExperienceTitle")}</h4>
                <p className="text-sm leading-snug">
                  {t("aboutPage.companyExperienceBody")}
                </p>
              </div>

              <div className="grid sm:grid-cols-3 gap-3 mt-5">
                <div className="rounded-lg p-3 bg-orange-50 dark:bg-slate-700/60 text-center">
                  <p className="text-xl font-bold text-orange-600 dark:text-orange-300">10+</p>
                  <p className="text-xs text-slate-600 dark:text-slate-300">{t("aboutPage.yearsExperience")}</p>
                </div>
                <div className="rounded-lg p-3 bg-orange-50 dark:bg-slate-700/60 text-center">
                  <p className="text-xl font-bold text-orange-600 dark:text-orange-300">100+</p>
                  <p className="text-xs text-slate-600 dark:text-slate-300">{t("aboutPage.happyBusinesses")}</p>
                </div>
                <div className="rounded-lg p-3 bg-orange-50 dark:bg-slate-700/60 text-center">
                  <p className="text-xl font-bold text-orange-600 dark:text-orange-300">24/7</p>
                  <p className="text-xs text-slate-600 dark:text-slate-300">{t("aboutPage.customerSupport")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="py-8 md:py-10 bg-white/70 dark:bg-slate-900/60 backdrop-blur-sm relative">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-50/40 to-orange-50/40 dark:from-slate-900/50 dark:to-slate-800/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <VideoTutorial />
        </div>
      </div>
    </>
  );

  const FeaturesPage = () => {
    const featureListRaw = t("features.featureItems", { returnObjects: true });
    const reportListRaw = t("features.reportItems", { returnObjects: true });
    const featureList = Array.isArray(featureListRaw) ? featureListRaw : [];
    const reportList = Array.isArray(reportListRaw) ? reportListRaw : [];

    const highlights = [
      { title: t("features.highlight1Title"), desc: t("features.highlight1Desc"), icon: "💳" },
      { title: t("features.highlight2Title"), desc: t("features.highlight2Desc"), icon: "📈" },
      { title: t("features.highlight3Title"), desc: t("features.highlight3Desc"), icon: "📑" },
    ];

    return (
      <>
        <div className="py-5 md:py-6 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-orange-50/40 via-transparent to-amber-50/40 dark:from-slate-900/60 dark:to-slate-800/50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <CompactPageTitle>{t("features.title")}</CompactPageTitle>
            <p className="text-center text-sm md:text-base text-slate-600 dark:text-slate-300 max-w-5xl mx-auto mb-6 leading-snug px-1">
              {t("features.intro")}
            </p>

            <div className="grid lg:grid-cols-2 gap-5 mb-6">
              <div className="bg-white/85 dark:bg-slate-800/85 backdrop-blur-sm rounded-2xl p-5 shadow-md border border-orange-100/70 dark:border-slate-700 min-w-0">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">{t("features.coreFeatures")}</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {featureList.map((feature) => (
                    <div
                      key={feature}
                      className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 bg-orange-50/70 dark:bg-slate-700/60 text-slate-700 dark:text-slate-200"
                    >
                      <span className="text-emerald-600 dark:text-emerald-400 shrink-0">✓</span>
                      <span className="text-xs sm:text-sm font-medium leading-snug break-words">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white/85 dark:bg-slate-800/85 backdrop-blur-sm rounded-2xl p-5 shadow-md border border-orange-100/70 dark:border-slate-700 min-w-0">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">{t("features.reports")}</h3>
                <div className="space-y-2">
                  {reportList.map((report) => (
                    <div
                      key={report}
                      className="flex flex-wrap sm:flex-nowrap items-center justify-between rounded-lg px-3 py-2 bg-slate-50/80 dark:bg-slate-700/60 text-slate-700 dark:text-slate-100 gap-2"
                    >
                      <span className="text-sm font-medium leading-snug min-w-0 break-words">{report}</span>
                      <span className="text-[10px] sm:text-xs uppercase tracking-wide text-orange-600 dark:text-orange-300 shrink-0">
                        {t("features.included")}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 rounded-xl p-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md">
                  <p className="text-sm md:text-base font-semibold leading-snug">{t("features.bottomBanner")}</p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {highlights.map((item) => (
                <div
                  key={item.title}
                  className="bg-white/85 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl p-4 shadow-md border border-orange-100/70 dark:border-slate-700 min-w-0"
                >
                  <div className="text-2xl mb-1.5">{item.icon}</div>
                  <h4 className="text-base font-bold text-slate-900 dark:text-white mb-1">{item.title}</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-snug">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="py-8 md:py-10 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-orange-50/40 to-transparent dark:via-slate-800/50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <CompactPageTitle>{t("screenshots")}</CompactPageTitle>
            <p className="text-center text-sm md:text-base text-slate-600 dark:text-slate-300 max-w-3xl mx-auto mb-6 leading-snug px-1">
              {t("screenshotsDescription")}
            </p>
            <ScreenshotSlider />
          </div>
        </div>
      </>
    );
  };

  const DownloadPage = () => (
    <div className="py-5 md:py-6 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-orange-50/30 to-transparent dark:via-slate-800/30" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <CompactPageTitle>{t("downloadPage.title")}</CompactPageTitle>
        <p className="text-center text-sm md:text-base text-slate-600 dark:text-slate-300 max-w-3xl mx-auto mb-6 leading-snug px-1">
          {t("installationDescription")}
        </p>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-start">
          {/* Left Side - Download Button */}
          <div className="bg-white/85 dark:bg-slate-800/85 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-orange-100/70 dark:border-slate-700">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">{t("download")}</h3>
            <button
              onClick={handleDownload}
              className="group relative inline-flex w-full max-w-full items-center justify-center px-6 py-4 text-base sm:px-8 sm:py-5 sm:text-lg font-semibold text-white bg-gradient-to-r from-green-500 via-emerald-500 to-teal-600 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-500 ease-in-out overflow-hidden mb-6">
              <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <svg className="w-6 h-6 sm:w-7 sm:h-7 mr-2 sm:mr-4 shrink-0 group-hover:animate-bounce relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="relative z-10 text-center leading-snug">{t("download")} {t("home.downloadSuffix")}</span>
            </button>
            <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {t("supportedOS")}
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                {t("fileSize")}
              </div>
            </div>
          </div>

          {/* Right Side - Installation Steps */}
          <div className="bg-white/85 dark:bg-slate-800/85 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-orange-100/70 dark:border-slate-700">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">{t("installationSteps")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { step: "1", title: t("step1Title"), desc: t("step1Desc"), icon: "📥", color: "from-blue-500 to-blue-600" },
                { step: "2", title: t("step2Title"), desc: t("step2Desc"), icon: "⚙️", color: "from-purple-500 to-purple-600" },
                { step: "3", title: t("step3Title"), desc: t("step3Desc"), icon: "🔧", color: "from-green-500 to-green-600" },
                { step: "4", title: t("step4Title"), desc: t("step4Desc"), icon: "🚀", color: "from-pink-500 to-pink-600" },
              ].map((item, index) => (
                <div key={index} className="text-center relative">
                  <div className="relative inline-block">
                    <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br ${item.color} rounded-xl text-white mb-2 shadow-md`}>
                      <span className="text-xl">{item.icon}</span>
                    </div>
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center shadow text-[10px]">
                      <span className="font-bold text-gray-700 dark:text-gray-300">{item.step}</span>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1 leading-snug">{item.title}</h3>
                  <p className="text-slate-600 dark:text-slate-300 text-xs leading-snug">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const SupportPage = () => {
    const [formData, setFormData] = React.useState({
      name: '',
      email: '',
      phone: '',
      reason: '',
      product: '',
      message: ''
    });
    const [selectedFiles, setSelectedFiles] = React.useState([]);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [submitStatus, setSubmitStatus] = React.useState(null);

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    };

    const handleFileChange = (e) => {
      const files = Array.from(e.target.files);
      setSelectedFiles(files);
    };

    const convertFileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      setSubmitStatus(null);

      try {
        // Convert files to base64
        const base64Files = await Promise.all(
          selectedFiles.map(file => convertFileToBase64(file))
        );

        const response = await fetch('/api/support', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...formData,
            files: base64Files,
            fileNames: selectedFiles.map(f => f.name)
          }),
        });

        const result = await response.json();

        if (result.success) {
          setSubmitStatus({ type: 'success', message: result.message });
          setFormData({
            name: '',
            email: '',
            phone: '',
            reason: '',
            product: '',
            message: ''
          });
          setSelectedFiles([]);
        } else {
          setSubmitStatus({ type: 'error', message: result.message });
        }
      } catch (error) {
        setSubmitStatus({ 
          type: 'error', 
          message: 'Failed to send message. Please try again later.' 
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="py-5 md:py-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-orange-50/30 to-transparent dark:via-slate-800/30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CompactPageTitle>{t("supportPage.title")}</CompactPageTitle>
          <div className="bg-white/80 dark:bg-slate-900/70 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-lg border border-orange-100/70 dark:border-slate-700">
            <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 lg:items-start">
              <div className="text-center lg:text-left space-y-3 min-w-0 lg:border-r border-orange-200/70 dark:border-slate-700 lg:pr-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center lg:justify-start gap-3">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-slate-800 via-orange-600 to-amber-500 dark:from-white dark:via-orange-300 dark:to-amber-200 bg-clip-text text-transparent leading-tight px-1">
                    {t("supportPage.heading")}
                  </h3>
                  <img
                    src="/24.png"
                    alt={t("supportPage.supportAlt")}
                    className="w-20 sm:w-32 h-auto object-contain mix-blend-multiply dark:mix-blend-lighten mx-auto sm:mx-0 shrink-0"
                  />
                </div>
                <p className="text-sm md:text-base text-slate-600 dark:text-slate-300 leading-snug max-w-xl mx-auto lg:mx-0 px-1">
                  {t("supportPage.intro")}
                </p>
                <div className="grid sm:grid-cols-2 gap-4 pt-1">
                  <div className="space-y-2 text-sm text-slate-700 dark:text-slate-200 text-center lg:text-left w-full max-w-sm mx-auto lg:mx-0 lg:max-w-none min-w-0">
                    <p className="flex flex-wrap items-center gap-2 justify-center lg:justify-start">
                      <FaPhone className="text-orange-500 shrink-0 transform scale-x-[-1]" />
                      <span>+92 314 5832291</span>
                    </p>
                    <p className="flex flex-wrap items-center gap-2 justify-center lg:justify-start break-all">
                      <FaEnvelope className="text-orange-500 shrink-0" />
                      <span>info.businessfinancepk@gmail.com</span>
                    </p>
                    <p className="flex items-start gap-2 justify-center lg:justify-start">
                      <FaMapMarkerAlt className="text-orange-500 shrink-0 mt-0.5" />
                      <span>{t("footer.locationDetail")}</span>
                    </p>
                    <div className="flex gap-2 justify-center lg:justify-start pt-1">
                      <a
                        href="https://instagram.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                        aria-label="Instagram"
                      >
                        <FaInstagram className="text-sm" />
                      </a>
                      <a
                        href="https://facebook.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                        aria-label="Facebook"
                      >
                        <FaFacebook className="text-sm" />
                      </a>
                      <a
                        href="https://youtube.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 rounded-full bg-gradient-to-br from-red-500 to-red-700 text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                        aria-label="YouTube"
                      >
                        <FaYoutube className="text-sm" />
                      </a>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden border border-orange-200/70 dark:border-slate-700 shadow-sm">
                  <iframe
                    title={t("supportPage.mapTitle")}
                    src="https://www.google.com/maps?q=Khairpur%20Mirs,%20Sindh,%20Pakistan&output=embed"
                    width="100%"
                    height="250"
                    className="block w-full h-[250px]"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>

              <div className="min-w-0">
                {submitStatus && (
                  <div className={`mb-4 p-4 rounded-lg text-sm ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-100 border border-green-400 text-green-700 dark:bg-green-900/30 dark:border-green-600 dark:text-green-300' 
                      : 'bg-red-100 border border-red-400 text-red-700 dark:bg-red-900/30 dark:border-red-600 dark:text-red-300'
                  }`}>
                    {submitStatus.message}
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">{t("supportPage.nameLabel")}</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full min-h-[2.75rem] rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-700/70 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-400"
                      placeholder={t("supportPage.placeholderName")}
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">{t("supportPage.emailLabel")}</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full min-h-[2.75rem] rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-700/70 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-400"
                        placeholder={t("supportPage.placeholderEmail")}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">{t("supportPage.phoneLabel")}</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full min-h-[2.75rem] rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-700/70 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-400"
                        placeholder={t("supportPage.placeholderPhone")}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">{t("supportPage.reasonLabel")}</label>
                    <select
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      required
                      className="w-full min-h-[2.75rem] rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-700/70 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    >
                      <option value="" disabled>
                        {t("supportPage.selectReason")}
                      </option>
                      <option value="inquiry">{t("supportPage.inquiry")}</option>
                      <option value="contact">{t("supportPage.contact")}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">{t("supportPage.productLabel")}</label>
                    <select
                      name="product"
                      value={formData.product}
                      onChange={handleInputChange}
                      required
                      className="w-full min-h-[2.75rem] rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-700/70 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    >
                      <option value="" disabled>
                        {t("supportPage.selectProduct")}
                      </option>
                      <option value="desktop-application">{t("supportPage.desktopApplication")}</option>
                      <option value="mobile-application">{t("supportPage.mobileApplication")}</option>
                      <option value="website">{t("supportPage.website")}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">{t("supportPage.uploadLabel")}</label>
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*,.pdf,video/*"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        id="file-input"
                      />
                      <label htmlFor="file-input" className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-700/70 px-3 py-2 text-xs text-slate-700 dark:text-slate-200 flex items-center justify-between hover:border-orange-400/70 dark:hover:border-orange-500 transition-colors cursor-pointer group">
                        <span className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-semibold px-2 py-1 rounded-full mr-2 shrink-0 flex items-center gap-1 group-hover:from-orange-600 group-hover:to-amber-600 transition-all">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          {selectedFiles.length > 0 ? `Files ${selectedFiles.length}` : 'Choose Files'}
                        </span>
                        <span className="truncate">
                          {selectedFiles.length > 0 ? selectedFiles.map((file, index) => (
                            <span key={index}>
                              {file.name}
                              {index < selectedFiles.length - 1 ? ', ' : ''}
                            </span>
                          )) : 'Choose Files'}
                        </span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">{t("supportPage.messageLabel")}</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={3}
                      className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-700/70 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-y min-h-[4.5rem]"
                      placeholder={t("supportPage.placeholderMessage")}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-semibold py-2.5 shadow-md hover:from-orange-600 hover:to-amber-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        {t("supportPage.sendMessage")}
                      </span>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-slate-100 dark:bg-gradient-to-br dark:from-slate-950 dark:via-slate-900 dark:to-slate-800 min-h-screen flex flex-col overflow-x-hidden">
      <Header navLinks={pageLinks} />

      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <a
        href="https://wa.me/923145832291?."
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-robot-float"
        aria-label="Chat on WhatsApp"
      >
        <img
          src="/whatsapp-robot-business-finance.svg"
          alt="Business Finance WhatsApp Robot"
          className="w-20 h-20 sm:w-24 sm:h-24 drop-shadow-2xl"
        />
      </a>

      <Footer />
    </div>
  );
}

export default App;
